<template>
  <div ref="rootEl" v-click-outside="close" :class="rootClasses">
    <div ref="triggerEl" class="ak-popover__trigger" @click="togglePopover">
      <slot name="trigger" />
    </div>
    <template v-if="showOverlay">
      <div ref="overlayEl" :class="overlayClasses" @click="close"></div>
    </template>
    <template v-if="showPopover">
      <div ref="tooltipEl" :class="popoverClasses" :style="popoverStyles">
        <span
          ref="arrowEl"
          class="ak-popover__arrow"
          data-popper-arrow="true"
        ></span>
        <slot name="popover" :close="close" />
      </div>
    </template>
  </div>
</template>

<script>
import { defineComponent, computed, ref, nextTick, onMounted } from 'vue'

import clickOutside from '../../directives/clickOutside/index'
import { createPopper } from '@popperjs/core'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-popover />
   */
  name: 'AkPopover',

  /**
   * @type {Object}
   * @description A collection of directives that this component is
   * dependant on.
   */
  directives: {
    clickOutside,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description Determines the position of the tooltip
     */
    placement: {
      validator: (o) =>
        [
          'auto',
          'auto-start',
          'auto-end',
          'top',
          'top-start',
          'top-end',
          'bottom',
          'bottom-start',
          'bottom-end',
          'right',
          'right-start',
          'right-end',
          'left',
          'left-start',
          'left-end',
        ].indexOf(o) >= 0,
      type: String,
      default: 'auto',
    },

    /**
     * @type {Number}
     * @description The width of the popover
     */
    width: {
      type: String,
      default: 'none',
    },

    /**
     * @type {String}
     * @description The amount of padding around the content
     */
    size: {
      validator: (o) =>
        ['none', 'tiny', 'small', 'normal', 'large'].indexOf(o) >= 0,
      type: String,
      default: 'small',
    },

    /**
     * @type {String}
     * @description The type of event to trigger the popover on
     */
    trigger: {
      validator: (o) => ['click'].indexOf(o) >= 0,
      type: String,
      default: 'click',
    },

    /**
     * @type {Boolean}
     * @description Show the overlay to prevent accidental hovers
     */
    overlay: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Overlay has a background
     */
    overlayBg: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Determines the color of the popover background.
     */
    theme: {
      validator: (o) => ['light', 'dark'].indexOf(o) >= 0,
      type: String,
      default: 'light',
    },

    showOnLoad: {
      type: Boolean,
      default: false,
    },

    openOnce: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['open', 'close'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const showPopover = ref(false)
    const rootEl = ref(null)
    const triggerEl = ref(null)
    const tooltipEl = ref(null)
    const overlayEl = ref(null)
    const popper = ref(null)
    const opens = ref(0)

    /**
     * @type {Object}
     * @desctiption A computed property to determine if the popoer should
     * have an overlay displayed
     */
    const showOverlay = computed(() => {
      return showPopover.value && props.overlay
    })

    /**
     * @returns {Object}
     * @description Returns the options for the popover
     */
    const options = computed(() => {
      return {
        placement: props.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
          {
            name: 'arrow',
            options: {
              padding: 5, // 5px from the edges of the popper
            },
          },
        ],
      }
    })

    const rootClasses = computed(() => {
      return {
        'ak-popover': true,
        [`ak-popover--${props.theme}`]: true,
        'ak-popover--is-open': showPopover.value,
        'ak-popover--has-overlay': props.showOverlay,
      }
    })

    const popoverClasses = computed(() => {
      const classes = ['ak-popover__content', ``]
      classes.push(`ak-popover__content--${props.size}`)
      return classes
    })

    const overlayClasses = computed(() => {
      const classes = ['ak-popover__overlay']
      if (props.overlayBg) classes.push('ak-popover__overlay--has-bg')
      return classes
    })

    const popoverStyles = computed(() => {
      return { width: props.width }
    })

    const createInstance = () => {
      popper.value = createPopper(
        triggerEl.value,
        tooltipEl.value,
        options.value
      )
    }

    const removeInstance = () => {
      popper.value.destroy()
      popper.value = null
    }

    const togglePopover = () => {
      showPopover.value === true ? close() : open()
    }

    const open = () => {
      if (props.openOnce && opens.value > 0) return
      opens.value++
      showPopover.value = true
      nextTick(() => {
        createInstance()
        emit('open')
      })
    }

    const close = () => {
      if (showPopover.value === true) {
        showPopover.value = false
        nextTick(() => {
          removeInstance()
          emit('close')
        })

        return true
      }

      return false
    }

    onMounted(() => {
      if (props.showOnLoad === true) {
        open()
      }
    })

    return {
      rootClasses,
      popoverClasses,
      overlayClasses,
      popoverStyles,
      showPopover,
      showOverlay,
      togglePopover,
      open,
      close,
      rootEl,
      triggerEl,
      tooltipEl,
      overlayEl,
      popper,
      options,
      opens,
    }
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
@import '../../scss/_settings.scss';

$popover-arrow-size: 10px;

.ak-popover {
  $self: &;
  display: inline-block;

  &__trigger {
    display: block;
  }

  &__content {
    font-size: $base-font-size;
    display: block;
    box-shadow: $base-shadow-large;
    background-color: #fff;
    border-radius: $base-border-radius;
    z-index: 100;
    max-width: 90vw;
    text-align: left;

    &--tiny {
      padding: $spacing-tiny;
    }
    &--small {
      padding: $spacing-small;
    }
    &--normal {
      padding: $spacing;
    }
    &--large {
      padding: $spacing-large;
    }

    // Theme
    #{$self}--dark & {
      background-color: $color-grey-dark;
      color: #fff;
    }
  }

  &__arrow {
    &,
    &::before {
      position: absolute;
      width: $popover-arrow-size;
      height: $popover-arrow-size;
      z-index: -1;
    }

    &::before {
      content: '';
      transform: rotate(45deg);
      background-color: #fff;
      // Theme
      #{$self}--dark & {
        background-color: $color-grey-dark;
      }
    }
    [data-popper-placement^='top'] & {
      bottom: calc(#{-$popover-arrow-size} / 2);
    }
    [data-popper-placement^='bottom'] & {
      top: calc(#{-$popover-arrow-size} / 2);
    }
    [data-popper-placement^='left'] & {
      right: calc(#{-$popover-arrow-size} / 2);
    }
    [data-popper-placement^='right'] & {
      left: calc(#{-$popover-arrow-size} / 2);
    }
  }

  &__overlay {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    &--has-bg {
      background-color: rgba($color-grey-dark, 0.75);
    }
  }
}
</style>
